import React, { isValidElement, memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { fetchWebsiteSettings, fetchQueries, fetchCurrentUser } from 'store';
import { HijackTopbar } from 'pages/users/components';
import Error429 from 'pages/user/error-429/Error429';
import { Breadcrumbs } from 'components';
import NetworkStatusBanner from './network-status/NetworkStatusBanner';
import LayoutSidebar from './sidebar/LayoutSidebar';
import LayoutHeader from './header/LayoutHeader';
import BackToTopButton from './back-to-top-button/BackToTopButton';
import { StyledLayout } from './Layout.styles';

/**
 * Determines if the current page should have the main layout structure.
 * Pages like login, registration, PDF viewer, etc. should not have the layout.
 *
 * @function hasLayout
 * @returns {boolean} True if the current page should have layout, false otherwise
 */
function hasLayout() {
  return !window.location.pathname.match(
    /\/login|\/change-password|\/forgot-password|\/pdf|\/register|\/onboarding|\/changelog/g
  );
}

/**
 * Main layout component that provides the application's structural framework.
 * Handles conditional rendering based on route, user state, and application settings.
 *
 * Features:
 * - Conditional layout rendering based on current route
 * - Website settings synchronization on authentication
 * - Rate limiting error handling (Error 429)
 * - User hijacking notification display
 * - Responsive sidebar management
 * - Left filters panel integration
 * - Breadcrumb navigation
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} [props.children] - Child components to render within the layout
 * @returns {JSX.Element} The layout structure or direct children for non-layout pages
 *
 * @example
 * // Basic usage with children
 * <Layout>
 *   <YourPageComponent />
 * </Layout>
 *
 * @example
 * // Usage with React Router Outlet (children will be undefined)
 * <Layout />
 */
const Layout = ({ children }) => {
  // UI State selectors
  /** @type {boolean} Whether the sidebar menu is in expanded state */
  const isSidebarMenuExpanded = useSelector(
    (state) => state.pageReducer.isSidebarMenuExpanded
  );

  // Settings and sync state selectors
  /** @type {boolean} Whether website settings have been synchronized */
  const isWebsiteSettingsSynced = useSelector(
    (state) => state.settingsReducer.isWebsiteSettingsSynced
  );
  /** @type {boolean} Whether initial queries have been synchronized */
  const isQueriesSynced = useSelector(
    (state) => state.pageReducer.isQueriesSynced
  );

  // User state selectors
  /** @type {boolean} Whether the current user session is hijacked by an admin */
  const isHijacked = useSelector(
    (state) => state.accountReducer.currentUser?.is_hijacked
  );
  /** @type {boolean} Whether the user is currently authenticated */
  const isLoggedIn = useSelector((state) => state.authReducer.isLoggedIn);

  // Error state selectors
  /** @type {boolean} Whether the user is rate limited (HTTP 429 error active) */
  const isLimited = useSelector(
    (state) => state.errorReducer?.error_429?.active
  );

  const dispatch = useDispatch();

  // Online status selector
  /** @type {boolean} Whether the user is online */
  const isClientOnline = useSelector((state) => state.pageReducer.onlineStatus);

  /**
   * Effect to synchronize website settings when user is logged in
   * but website settings are not yet synced.
   */
  useEffect(() => {
    async function fetchData() {
      if (!isQueriesSynced) {
        await dispatch(fetchQueries()).unwrap();
      }
      if (!isWebsiteSettingsSynced && isLoggedIn) {
        await dispatch(
          fetchWebsiteSettings({ shouldApplyToOtherReducers: true })
        ).unwrap();
      }
      if (isLoggedIn) {
        await dispatch(fetchCurrentUser()).unwrap();
      }
    }
    fetchData();
  }, [dispatch, isWebsiteSettingsSynced, isQueriesSynced, isLoggedIn]);

  // Early return for pages that shouldn't have layout structure
  if (isValidElement(children) && !hasLayout()) {
    return children;
  }

  return (
    <StyledLayout>
      {/* <Topbar button={false} description="ویژگی ابرجستوجوگر به صورت آزمایشی برای همه کاربران فعال شده است و پس از پایان مدت آزمایشی تنها کاربرانی که اشتراکی شامل این ویژگی دارند میتوانند از آن استفاده کنند."/>
       */}
      {/* <InfoModal /> */}

      {/* Show hijack notification bar when user session is hijacked by admin */}
      {isHijacked && <HijackTopbar />}

      {/* Show rate limit error page or main layout based on error state */}
      {isLimited ? (
        <Error429 />
      ) : (
        <div
          className={classNames(
            'layout',
            isSidebarMenuExpanded
              ? 'is-sidebar-menu-expanded'
              : 'is-sidebar-menu-collapsed'
          )}
        >
          {/* Left sidebar navigation */}
          <LayoutSidebar />

          {/* Main content area */}
          <div className='layout-main'>
            {/* Top header bar */}
            <LayoutHeader />
            {!isClientOnline && <NetworkStatusBanner />}
            {/* Main page content container */}
            <div id='page-container' className='layout-body'>
              {/* Breadcrumb navigation */}
              <Breadcrumbs />

              {/* Page content - either passed children or React Router Outlet */}
              {children || <Outlet />}
            </div>
          </div>

          <BackToTopButton />
        </div>
      )}
    </StyledLayout>
  );
};

Layout.propTypes = {
  children: PropTypes.node,
};
export default memo(Layout);
